/*=========================================================================================
  File Name: vuesaxFormA11y.js
  Description: Global mixin that gives vuesax's form controls an accessible name.

               vuesax renders the visible text of <vs-checkbox> / <vs-radio> as a SIBLING
               of the native <input>, never as a <label> bound to it, and <vs-input> /
               <vs-textarea> render their floating "label-placeholder" as a decorative
               <span>. axe-core reports every one of them as "Form elements must have
               labels" (critical) - the audit's 13.1 "Form control has no label" and 13.2
               "Form control has incomplete label".

               Rather than fork vuesax or touch several hundred call sites, this derives a
               name from what is already on screen and puts it on the native control, so
               the visible text and the announced name always agree.
==========================================================================================*/

const NAMED = ['VsCheckbox', 'VsRadio', 'VsInput', 'VsTextarea', 'VsSelect'];

const CONTROL = 'input:not([type="hidden"]), textarea, select';

// An accessible name already present by any of the usual means.
function hasName(el) {
  if (el.getAttribute('aria-label')) return true;
  if (el.getAttribute('aria-labelledby')) return true;
  if (el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`)) return true;
  if (el.closest('label')) return true;
  if (el.getAttribute('title')) return true;
  return false;
}

function deriveName(vm, el) {
  // 1. Anything the component was given explicitly.
  const props = vm.$props || {};
  const explicit =
    props.label || props.labelPlaceholder || props['label-placeholder'] || props.placeholder;
  if (explicit) return String(explicit).trim();

  // 2. The component's own visible text (checkbox / radio slot content).
  const text = (vm.$el.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return text.slice(0, 120);

  // 3. vuesax's own rendered label span, if it produced one.
  const span = vm.$el.querySelector('.vs-input--label, .vs-select--label, .input-span-placeholder');
  if (span && span.textContent.trim()) return span.textContent.replace(/\s+/g, ' ').trim();

  // 4. The native control's own placeholder.
  const ph = el.getAttribute('placeholder');
  return ph ? ph.trim() : '';
}

// (Font-icon ligature text is hidden document-wide by utils/a11yDom.js.)

function enhance(vm) {
  if (!vm.$el || !vm.$el.querySelectorAll) return;
  vm.$el.querySelectorAll(CONTROL).forEach((el) => {
    if (hasName(el)) return;
    const name = deriveName(vm, el);
    if (name) el.setAttribute('aria-label', name);
  });
}

function run(vm) {
  if (NAMED.indexOf(vm.$options.name) !== -1) {
    vm.$nextTick(() => enhance(vm));
  }
}

export default {
  mounted() {
    run(this);
  },
  updated() {
    run(this);
  },
};
